import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { IonText } from '@ionic/react';
import classNames from 'classnames';
import { uniqBy } from 'lodash';
import { Swiper as SwiperJS, Autoplay, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useDispatch, useSelector } from 'src/store';
import { useBreakpoint } from 'src/hooks';
import { selectProduct, openProductDetails } from 'src/slices/product';
import { CampaignJoinInfoDto, ProductJoinInfoDto } from 'src/types/generated';
import { CampaignTypes } from 'src/types/interfaces';
import { trackRecommendationImpression, trackRecommendationClick } from 'src/utils/analytics';
import { formatPrice } from 'src/utils';
import classes from './CartRecommendations.module.scss';
import textClasses from 'src/styles/text.module.scss';

SwiperJS.use([Autoplay, Navigation, Pagination]);

const SESSION_IMPRESSION_PREFIX = 'pk_rec_impression_';

interface CampaignBlockProps {
  campaign: CampaignJoinInfoDto;
  bannerUrl: string;
  products: ProductJoinInfoDto[];
  isDesktopView: boolean;
  onProductClick: (campaign: CampaignJoinInfoDto, product: ProductJoinInfoDto) => void;
}

// Fires one impression the first time the campaign scrolls into view, once per session per campaign.
const CampaignBlock: React.FC<CampaignBlockProps> = ({
  campaign,
  bannerUrl,
  products,
  isDesktopView,
  onProductClick,
}) => {
  const blockRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    const node = blockRef.current;
    const key = `${SESSION_IMPRESSION_PREFIX}${campaign.id}`;
    if (!node || sessionStorage.getItem(key)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, '1');
            trackRecommendationImpression(campaign);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [campaign]);

  const onImgLoad = () => swiperRef?.current?.swiper?.update();

  return (
    <div ref={blockRef} className={classes.campaignBlock}>
      <img src={bannerUrl} alt={`Campaign ${campaign.name}`} className={classes.banner} onLoad={onImgLoad} />
      <Swiper
        className={classes.carousel}
        spaceBetween={12}
        navigation={isDesktopView}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        breakpoints={{
          // Fractional slidesPerView peeks the next card as a "scroll for more" cue.
          0: { slidesPerView: 2.2 },
          768: { slidesPerView: 4.4 },
        }}
        // @ts-ignore swiper ref typing
        ref={swiperRef}
      >
        {products.map((product) => (
          <SwiperSlide key={product.id} className={classes.slide} onClick={() => onProductClick(campaign, product)}>
            <div className={classes.tile}>
              {product.imageThumbnailUrl && (
                <img
                  src={product.imageThumbnailUrl}
                  alt={product.name}
                  className={classes.tileImage}
                  onLoad={onImgLoad}
                />
              )}
              <span className={classNames(textClasses.textM, classes.tileName)}>{product.name}</span>
              <IonText color="primary" className={classNames(textClasses.textL, classes.tilePrice)}>
                {formatPrice(product.price)}
              </IonText>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const CartRecommendations: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { isDesktopView } = useBreakpoint();
  const campaigns = useSelector((state) => state.campaign.campaigns);
  const products = useSelector((state) => state.product.products);
  const selectedLanguageId = useSelector((state) => state.system.selectedLanguageId);

  const imageType = isDesktopView ? 'desktop' : 'mobile';

  // Newest-first is admin-controlled via orderNumber, matching other campaign types.
  const renderableCampaigns = (campaigns ?? [])
    .filter((c) => c.type === CampaignTypes.RECOMMENDATION)
    .slice()
    .sort((a, b) => a.orderNumber - b.orderNumber)
    .map((campaign) => {
      const banner = campaign.images.find((i) => i.imageType === imageType && i.languageId === selectedLanguageId);
      // Join to salepoint-filtered products; drop anything the kiosk doesn't sell.
      // Recommendations take priority over AddOnSales; the dedupe lives in AddOnSales, not here.
      const campaignProducts = uniqBy(
        campaign.products
          .map((cp) => (products ?? []).find((p) => p.id === cp.productId))
          .filter((p): p is ProductJoinInfoDto => !!p),
        'id'
      );
      return { campaign, bannerUrl: banner?.imageFullUrl, products: campaignProducts };
    })
    .filter((c) => !!c.bannerUrl && c.products.length > 0);

  if (!products || renderableCampaigns.length === 0) return null;

  const onProductClick = (campaign: CampaignJoinInfoDto, product: ProductJoinInfoDto) => {
    trackRecommendationClick(campaign, product);
    dispatch(selectProduct(product.id));
    if (isDesktopView) {
      dispatch(openProductDetails(true));
    } else {
      history.push('/tabs/menu/details', { from: 'cart' });
    }
  };

  return (
    <div className="ion-margin-bottom">
      <h2 className={classNames(textClasses.titleBold, 'ion-margin-bottom')}>{t('Campaign recommendations')}</h2>
      {renderableCampaigns.map(({ campaign, bannerUrl, products: campaignProducts }) => (
        <CampaignBlock
          key={campaign.id}
          campaign={campaign}
          bannerUrl={bannerUrl as string}
          products={campaignProducts}
          isDesktopView={isDesktopView}
          onProductClick={onProductClick}
        />
      ))}
    </div>
  );
};

export default CartRecommendations;
